<script setup lang="tsx">
  import { PlusIcon } from '@heroicons/vue/solid';
  import type {
    ResponseStatus,
    InfoBlock,
    AdvancePaymentResponse,
    PaymentUpdateResponse,
    AdvanceSearchOptions,
    AdvancePaymentOptions,
    OrgBankPaymentOption
  } from '~/types';
  import DrawerLayout from '~/components/UI/Drawers/DrawerLayout.vue';
  import RoleBasedButton from '~/components/UI/Others/RoleBasedButton.vue';
  import CustomInput from '~/components/UI/Forms/CustomInput.vue';
  import CustomDate from '~/components/UI/Forms/CustomDate.vue';
  import CustomRupeeInput from '~/components/UI/Forms/CustomRupeeInput.vue';
  import CustomDropdown from '~/components/UI/Forms/CustomDropdown.vue';
  import CustomDropdownWithSearch from '~/components/UI/Forms/CustomDropdownWithSearch.vue';
  import { get, post } from '~/services/donationScreen';
  import { formatToIndianCurrency } from '~/functions/currencyUtilities';
  import type { Dayjs } from 'dayjs';
  import { bookOfAccounts, paymentModes, updatePaymentFormData } from '~/components/Expenses/Table/expenseTableUtils';
  import AuditLogs from '~/components/shared/RoutedDrawers/RoutedDrawerComponents/AuditLogs.vue';
  import { cloneDeep } from 'lodash';
  import dayjs from 'dayjs';

  const { onClose, identifier } = defineProps<{
    onClose: () => void;
    identifier: string;
  }>();
  const { role } = useAuth();
  const responseState = ref<ResponseStatus>('success');
  const getDropdownDataStatus = ref<ResponseStatus>('idle');
  const auditLogs = ref<any>();
  const formState = ref<InfoBlock[]>([]);

  const expenseDetails = reactive({
    totalAmountPaid: null,
    balancePayments: 0,
    balanceForFCRA: 0,
    balanceForNonFCRA: 0,
    remainingToBeAllocated: 0,
    totalInvoiceAmount: 0,
    totalExpenseAllocationNonFcra: 0,
    totalExpenseAllocationFcra: 0,
    totalUnallocatedTotalAmount: 0,
    totalExpenseAllocation: 0,
    tdsNonFcra: 0,
    tdsFcra: 0,
    unallocatedTdsAmount: 0,
    totalTdsAmount: 0,
    payableNetOfTdsNonFcra: 0,
    payableNetOfTdsFcra: 0,
    payableNetOfTdsUnallocated: 0,
    payableNetOfTds: 0,
    auditLog: [],
    grantsAndBudgetAllocation: []
  });
  // const activeKey = ref([]);
  const advanceSearchOptions = ref<AdvanceSearchOptions[]>([]);
  // Bank Details options — bucketed by FCRA / Non-FCRA, filtered per row to match that
  // payment's selected Book of Accounts. Optional field: not required on submit.
  const orgBanksList = ref<{ FCRA: OrgBankPaymentOption[]; 'Non-FCRA': OrgBankPaymentOption[] }>({
    FCRA: [],
    'Non-FCRA': []
  });
  const bankOptionsFor = (bookOfAccountsValue: string) => {
    const options = bookOfAccountsValue === 'Non-FCRA' ? orgBanksList.value['Non-FCRA'] : orgBanksList.value.FCRA;
    // Disabled banks stay registered (so a payment already pointing at one still resolves
    // its label instead of showing blank) but are hidden from the browsable/searchable
    // list so they can't be picked for a new selection.
    return options.map(bank => ({
      title: bank.label,
      value: bank.uid.toString(),
      hiddenFromList: bank.is_disabled
    }));
  };
  const updatePaymentStatus = ref<ResponseStatus>('idle');
  const paymentStatus = ref<any>({});
  const approvalStatus = ref<string>('');
  const is_current_approver = ref<boolean>(false);
  const isFinalApprover = ref<boolean>(false);
  const cancelled = ref<boolean>(false);

  const errorTimers = new Map<number, ReturnType<typeof setTimeout>>();

  const setPaymentError = (paymentId: number, error: string) => {
    if (errorTimers.has(paymentId)) clearTimeout(errorTimers.get(paymentId)!);
    formState.value = formState.value.map(p => (p.id === paymentId ? { ...p, error } : p));
    if (error) {
      const timer = setTimeout(() => {
        formState.value = formState.value.map(p => (p.id === paymentId ? { ...p, error: '' } : p));
        errorTimers.delete(paymentId);
      }, 6000);
      errorTimers.set(paymentId, timer);
    }
  };

  // const paymentsNonFcra = computed(() => {
  //   return formState.value.reduce((acc, payment) => {
  //     if (!payment.isAdvance && payment.bookOfAccounts === 'Non-FCRA') {
  //       return acc + (payment.amount || 0);
  //     }
  //     return acc;
  //   }, 0);
  // });

  // const paymentsFcra = computed(() => {
  //   return formState.value.reduce((acc, payment) => {
  //     if (!payment.isAdvance && payment.bookOfAccounts === 'FCRA') {
  //       return acc + (payment.amount || 0);
  //     }
  //     return acc;
  //   }, 0);
  // });

  // const totalPayments = computed(() => {
  //   return +paymentsNonFcra.value + +paymentsFcra.value;
  // });

  // const paymentsViaAdvancesNonFcra = computed(() => {
  //   return formState.value.reduce((acc, payment) => {
  //     if (payment.isAdvance && payment.bookOfAccounts === 'Non-FCRA') {
  //       return acc + (payment.amount || 0);
  //     }
  //     return acc;
  //   }, 0);
  // });

  // const paymentsViaAdvancesFcra = computed(() => {
  //   return formState.value.reduce((acc, payment) => {
  //     if (payment.isAdvance && payment.bookOfAccounts === 'FCRA') {
  //       return acc + (payment.amount || 0);
  //     }
  //     return acc;
  //   }, 0);
  // });

  // const totalPaymentsViaAdvances = computed(() => {
  //   return +paymentsViaAdvancesNonFcra.value + +paymentsViaAdvancesFcra.value;
  // });

  // const totalPaymentsAndAdvancesNonFcra = computed(() => {
  //   return totalPayments.value + totalPaymentsViaAdvances.value;
  // });

  // const totalPaymentsAndAdvancesFcra = computed(() => {
  //   return totalPayments.value + totalPaymentsViaAdvances.value;
  // });

  // const totalPaymentAndAdvancesBoth = computed(() => {
  //   return +totalPaymentsAndAdvancesNonFcra.value + +totalPaymentsAndAdvancesFcra.value;
  // });

  // const balanceNonFcra = computed(() => {
  //   return expenseDetails.payableNetOfTdsNonFcra - totalPaymentsAndAdvancesNonFcra.value;
  // });

  // const balanceFcra = computed(() => {
  //   return expenseDetails.payableNetOfTdsFcra - totalPaymentsAndAdvancesFcra.value;
  // });

  // const balanceBoth = computed(() => {
  //   return +expenseDetails.payableNetOfTds - +totalPaymentAndAdvancesBoth.value;
  // });

  const addPayment = (isAdvance: boolean) => {
    formState.value.push({
      id: Date.now(),
      isAdvance: isAdvance,
      advance: '',
      advancePaymentId: '',
      paymentDate: '',
      paymentMode: '',
      referenceNumber: '',
      bookOfAccounts: '',
      amount: null,
      amountPaid: null,
      outstanding: null,
      originalAmount: null,
      error: '',
      bankUid: ''
    });
  };

  const removePayment = (id: number) => {
    formState.value = formState.value.filter(payment => payment.id !== id);
  };

  // Once an advance payment line has been utilized by a linked Expense, changing any of
  // its fields here (including Bank Details) won't automatically update that Expense.
  // Warn the user once per line before applying the change; "Proceed" applies it and
  // remembers the confirmation for the rest of this editing session, "Cancel" discards it.
  const linkedChangeWarning = ref<{ visible: boolean; paymentId: number | null; pendingAction: (() => void) | null }>({
    visible: false,
    paymentId: null,
    pendingAction: null
  });
  const confirmedLinkedPayments = ref<Set<number>>(new Set());

  // formState is never mutated until the pending action runs (below), so Cancel already
  // leaves the real data untouched. But the row's input components keep their own local
  // display value and won't know to resync since the (unchanged) prop never changes.
  // Bumping this counter per-row forces those inputs to remount on Cancel, snapping the
  // field back to what's actually stored.
  const rowRevertKey = ref<Record<number, number>>({});

  const guardLinkedChange = (paymentId: number, action: () => void) => {
    const payment = formState.value.find(p => p.id === paymentId);
    if (payment?.isLinkedToExpense && !confirmedLinkedPayments.value.has(paymentId)) {
      linkedChangeWarning.value = { visible: true, paymentId, pendingAction: action };
      return;
    }
    action();
  };

  const confirmLinkedChange = () => {
    if (linkedChangeWarning.value.paymentId != null) {
      confirmedLinkedPayments.value.add(linkedChangeWarning.value.paymentId);
    }
    const pendingAction = linkedChangeWarning.value.pendingAction;
    linkedChangeWarning.value = { visible: false, paymentId: null, pendingAction: null };
    pendingAction?.();
  };

  const cancelLinkedChange = () => {
    const paymentId = linkedChangeWarning.value.paymentId;
    if (paymentId != null) {
      rowRevertKey.value[paymentId] = (rowRevertKey.value[paymentId] || 0) + 1;
    }
    linkedChangeWarning.value = { visible: false, paymentId: null, pendingAction: null };
  };

  const updateFormState = (value: string, id: number, key: string) => {
    guardLinkedChange(id, () => {
      formState.value = formState.value.map(payment => (payment.id === id ? { ...payment, [key]: value } : payment));
    });
  };

  const updateBookOfAccounts = (val: string, paymentId: number) => {
    guardLinkedChange(paymentId, () => {
      formState.value = formState.value.map(p => (p.id === paymentId ? { ...p, bookOfAccounts: val, bankUid: '' } : p));
    });
  };

  const updatePaymentMode = (val: string, paymentId: number) => {
    guardLinkedChange(paymentId, () => {
      formState.value = formState.value.map(p =>
        p.id === paymentId ? { ...p, paymentMode: val, bankUid: val === 'Cash' ? '' : p.bankUid } : p
      );
    });
  };

  const updateAdvanceHead = (val: string, paymentId: number) => {
    guardLinkedChange(paymentId, () => {
      formState.value = formState.value.map(p =>
        p.id === paymentId
          ? {
              ...p,
              advance: val,
              advancePaymentId: '',
              paymentDate: '',
              paymentMode: '',
              bookOfAccounts: '',
              referenceNumber: '',
              amount: null,
              amountPaid: null,
              outstanding: null
            }
          : p
      );
    });
  };

  const updateAdvancePaymentId = async (val: string, paymentId: number) => {
    const payment = formState.value.find(p => p.id === paymentId);
    if (!payment) return;
    guardLinkedChange(paymentId, async () => {
      const advancePayment = advanceSearchOptions.value
        .find(option => option.value === payment.advance)
        ?.advancePayments.find(item => item.value === val) as AdvancePaymentOptions;
      // Step 1: set all fields including bookOfAccounts, clear bankUid so options render first
      formState.value = formState.value.map(p =>
        p.id === paymentId
          ? {
              ...p,
              advancePaymentId: val,
              paymentDate: advancePayment?.paymentDate,
              paymentMode: advancePayment?.paymentMode,
              bookOfAccounts: advancePayment?.bookOfAccounts,
              referenceNumber: advancePayment?.referenceNumber,
              amountPaid: advancePayment?.amountPaid,
              outstanding: advancePayment?.outstanding
            }
          : p
      );
    });
  };

  const validateForm = (): Promise<boolean> => {
    let hasError = false;
    formState.value.forEach(payment => {
      const requiredFields = payment.isAdvance
        ? ['advancePaymentId', 'advance', 'paymentDate', 'paymentMode', 'bookOfAccounts', 'amount']
        : ['paymentDate', 'paymentMode', 'bookOfAccounts', 'amount'];

      const missingField = requiredFields.some(field => !payment[field as keyof InfoBlock]);
      let error = '';
      if (missingField) {
        error = 'All fields are required!';
      } else if (payment.isAdvance && payment.amount && payment.amount > (payment.outstanding || 0)) {
        error = 'Amount exceeds outstanding!';
      }
      if (error) {
        hasError = true;
        setPaymentError(payment.id, error);
      }
    });

    return hasError ? Promise.reject(false) : Promise.resolve(true);
  };

  const updatePayment = async () => {
    try {
      const isFormValid = await validateForm();
      if (!isFormValid) {
        return;
      }
      updatePaymentStatus.value = 'pending';
      const formData = updatePaymentFormData(formState.value);
      // if (paymentStatus?.value?.advances_against_expense) {
      //   updatePaymentStatus.value = 'error';
      //   return openFailedCustomNotification(
      //     'This Advance is linked with Expense so first delink them after that try to cancel the Advance.'
      //   );
      // } else {
      const response = await post<PaymentUpdateResponse>(`/v1/save_advance_payments_list/${identifier}`, {
        data: formData
      });

      if (response?.data?.status === 'success') {
        openSuccessfulCustomNotification(`Payment updated successfully!`);
        updatePaymentStatus.value = 'success';
        onClose();
      } else if (response?.data?.status === 'error') {
        updatePaymentStatus.value = 'error';
        openFailedCustomNotification(response?.data?.message);
      } else {
        updatePaymentStatus.value = 'error';
        openFailedCustomNotification('Something went wrong while updating payment!');
      }
      // }
    } catch (error) {
      console.error('Error updating payment:', error);
      updatePaymentStatus.value = 'error';
    }
  };

  onMounted(async () => {
    try {
      getDropdownDataStatus.value = 'pending';
      const advanceDropdownResponse = await get<AdvancePaymentResponse>('/v1/get_advance_payments_line_item');

      const advanceDropdownData = advanceDropdownResponse?.data?.data?.map(advanceOption => {
        const advancePaymentOptions: {
          title: string;
          value: string;
          advancePayments: {
            title: string;
            value: string;
            amountPaid: number;
            outstanding: number;
            paymentDate: Dayjs;
            paymentMode: string;
            referenceNumber: string;
            bookOfAccounts: string;
          }[];
        } = {
          title: advanceOption['advance-head'],
          value: advanceOption['advance-head'],
          advancePayments: []
        };

        advancePaymentOptions.advancePayments = Object.entries(advanceOption['advance-line-items']).map(
          ([key, payment]) => {
            const paymentIndex = parseInt(key);
            return {
              title: `${key}-${payment[`payment_date_${paymentIndex}`]}-${payment[`payment_mode_${paymentIndex}`]}`,
              value: `${key}-${payment[`payment_date_${paymentIndex}`]}-${payment[`payment_mode_${paymentIndex}`]}`,
              amountPaid: +payment[`payment_amount_${paymentIndex}`],
              outstanding: advanceOption.outstanding,
              paymentDate: dayjs(payment[`payment_date_${paymentIndex}`]),
              paymentMode: payment[`payment_mode_${paymentIndex}`],
              referenceNumber: payment[`payment_ref_${paymentIndex}`],
              bookOfAccounts: payment[`payment_fcra_status_${paymentIndex}`]
            };
          }
        );
        return advancePaymentOptions;
      });
      advanceSearchOptions.value = advanceDropdownData;
      orgBanksList.value = advanceDropdownResponse?.data?.org_banks_list || { FCRA: [], 'Non-FCRA': [] };
    } catch (error) {
      getDropdownDataStatus.value = 'error';
      openFailedCustomNotification('Something went wrong while fetching input data!');
      console.error('Error fetching data:', error);
    }
  });

  onMounted(async () => {
    try {
      const response = await get<any>(`/v1/get_advance/${identifier}`);
      const selectedExpense = response?.data?.data?.selected_advance;
      auditLogs.value = response?.data?.data?.audit_logs;
      approvalStatus.value = response?.data?.data?.selected_advance?.approval_status;
      is_current_approver.value = response?.data?.data?.selected_advance?.is_current_approver;
      isFinalApprover.value = response?.data?.data?.selected_advance?.is_final_approver;
      cancelled.value = response?.data?.data?.selected_advance?.is_cancelled;
      paymentStatus.value = response?.data?.data?.selected_advance.payment_stats;
      const { expDetails, payments } = selectedExpense;
      Object.assign(expenseDetails, cloneDeep(expDetails));
      const updatedPayments = payments.map((payment: any, index: number) => {
        // Create a new payment object with our component's field names
        const mappedPayment = {
          id: payment.id != null ? payment.id : Date.now() + index,
          isAdvance: payment.isAdvance || false,
          advance: payment.advance_uid || '',
          advancePaymentId: payment.advance_payment_li || '',
          paymentDate: dayjs(payment.payment_date) || '',
          paymentMode: payment.payment_mode || '',
          referenceNumber: payment.payment_ref || '',
          bookOfAccounts: payment.payment_fcra_status || '',
          amount: payment.payment_amount || null,
          amountPaid: 0,
          outstanding: 0,
          error: '',
          isLinkedToExpense: payment.is_linked_to_expense || false,
          bankUid: payment.payment_bank_uid || ''
        };

        if (mappedPayment.isAdvance) {
          const advancePayment = advanceSearchOptions.value.find(option => option.value === mappedPayment.advance);
          const advancePaymentOption = advancePayment?.advancePayments.find(
            item => item.value === mappedPayment.advancePaymentId
          );
          mappedPayment.amountPaid = advancePaymentOption?.amountPaid || 0;
          mappedPayment.outstanding = advancePaymentOption?.outstanding || 0;
        }
        return mappedPayment;
      });
      // Step 1: set payments with bankUid cleared so options render first
      formState.value = updatedPayments.map((p: any) => ({ ...p, bankUid: '' }));
      // Step 2: after options are registered, set bankUid so label lookup succeeds
      await nextTick();
      formState.value = formState.value.map((p, i) => ({ ...p, bankUid: updatedPayments[i].bankUid }));
    } catch (error: any) {
      openFailedCustomNotification('Something went wrong while fetching expense data!');
      console.error('Error while fetching expense data: ', error);
    }
  });

  onUnmounted(() => {
    errorTimers.forEach(timer => clearTimeout(timer));
    errorTimers.clear();
  });

  const isEditDisabled = computed(() => {
    const rolesList = role.value || [];
    const isSubmitter = rolesList.includes('advance_submitter');
    // const isApproverRole = rolesList.includes('advance_approver');
    const isPayeeAdmin = rolesList.includes('advance_admin');
    // const isNotSubmitted = approvalStatus.value === 'Not submitted for approval';
    const isAdmin = rolesList.includes('admin');
    // const isCurrentApprover = is_current_approver.value;
    const isLastApprover = isFinalApprover.value;
    const isApproved = approvalStatus.value === 'Approved';
    const isNoApprover = approvalStatus.value === '0 of 0';
    // const isCancelled = cancelled?.value

    if (isApproved && isSubmitter) {
      return true;
    }

    if (isNoApprover && isSubmitter) {
      return false;
    }

    if (isAdmin && (isApproved || isNoApprover)) {
      return false;
    }

    if (isLastApprover && (isApproved || isNoApprover)) {
      return false;
    }

    if (isNoApprover && isPayeeAdmin) {
      return false;
    }

    return true;
  });
</script>

<template>
  <p>Advance Drawer</p>
  <a-drawer
    v-if="identifier"
    class="monetary-drawer"
    :body-style="{ padding: '0px' }"
    :header-style="{
      padding: '12px',
      borderBottom: '1px solid #E4E4E7'
    }"
    title="Payment Information"
    :open="identifier !== null"
    width="1220"
    @close="onClose"
  >
    <template #extra>
      <div class="flex gap-2 items-center">
        <!-- <a-button
          class="flex rounded items-center gap-1 sm-medium py-1.5 px-2 justify-center shadow-[0_1px_2px_0px_#0000000D]"
          @click="onClose"
        >
          Cancel
        </a-button> -->
        <RoleBasedButton
          component-name="updatePaymentActionButton"
          active-class="!text-white hover:!text-white bg-blue-700 hover:bg-blue-600 disabled:!text-blue-300 disabled:border-blue-300 disabled:bg-blue-50 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-blue-700 hover:!border-blue-600 shadow-[0_1px_3px_0px_#0000001A,0_1px_2px_0_#0000000F]"
          disabled-class="flex rounded items-center !text-gray-300 gap-1 text-sm font-medium py-1.5 px-2 justify-center border !border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          :loading="updatePaymentStatus === 'pending'"
          :disabled="isEditDisabled"
          @click="updatePayment"
        >
          Update Payment
        </RoleBasedButton>
      </div>
    </template>
    <DrawerLayout
      :response-state="responseState"
      main-display="null"
      sub-display="null"
      side-display="null"
      no-header-display
      @close="onClose"
    >
      <template #column1>
        <div v-if="formState.length === 0" class="flex min-h-[243px] bg-gray-50 w-full justify-center items-center">
          <p class="sm-medium text-gray-500">No Payments Linked.</p>
        </div>
        <a-form
          v-else
          class="flex flex-col w-full p-3 gap-3 min-h-[243px] paymentManagement"
          :colon="false"
          :label-col="{ style: { width: '158px' } }"
          label-align="left"
          :hide-required-mark="true"
          :disabled="updatePaymentStatus === 'pending'"
          @submit="updatePayment"
        >
          <div
            v-for="payment in formState"
            :key="payment.id"
            class="flex border border-gray-200 rounded-lg shadow-[0_1px_4px_0px_#0C0C0D0D]"
          >
            <div class="flex flex-col gap-3 p-3 w-[430px] border-r border-gray-200">
              <CustomDropdownWithSearch
                v-if="payment.isAdvance"
                :key="`advance_${payment.id}_${rowRevertKey[payment.id] || 0}`"
                label-text="Advance"
                :value="payment.advance"
                :name="`advance_${payment.id}`"
                :is-compulsory="true"
                :state-key="`advance_${payment.id}`"
                :options="advanceSearchOptions"
                :disabled="updatePaymentStatus === 'pending'"
                :dropdown-match-select-width="false"
                dropdown-min-width="400px"
                @update:value="val => updateAdvanceHead(val, payment.id)"
              />
              <CustomDropdownWithSearch
                v-if="payment.isAdvance"
                :key="`advancePaymentId_${payment.id}_${rowRevertKey[payment.id] || 0}`"
                label-text="Advance Payment ID"
                :value="payment.advancePaymentId"
                :name="`advancePaymentId_${payment.id}`"
                :is-compulsory="true"
                :is-disabled="!payment.advance || updatePaymentStatus === 'pending'"
                :options="
                  advanceSearchOptions
                    .find(option => option.value === payment.advance)
                    ?.advancePayments.map(payment => ({
                      title: payment.title,
                      value: payment.value
                    })) || []
                "
                :dropdown-match-select-width="false"
                dropdown-min-width="400px"
                @update:value="val => updateAdvancePaymentId(val, payment.id)"
              />
              <CustomDate
                :key="`paymentDate_${payment.id}_${rowRevertKey[payment.id] || 0}`"
                label-text="Payment Date"
                :value="payment.paymentDate"
                :name="`paymentDate_${payment.id}`"
                :is-compulsory="true"
                :state-key="`paymentDate_${payment.id}`"
                :disabled="payment.isAdvance || updatePaymentStatus === 'pending'"
                @update:value="val => updateFormState(val, payment.id, 'paymentDate')"
              />
              <CustomDropdown
                :key="`paymentMode_${payment.id}_${rowRevertKey[payment.id] || 0}`"
                label-text="Payment Mode"
                :value="payment.paymentMode"
                :name="`paymentMode_${payment.id}`"
                :is-compulsory="true"
                :options="paymentModes"
                :state-key="`paymentMode_${payment.id}`"
                label-width="158px"
                :disabled="payment.isAdvance || updatePaymentStatus === 'pending'"
                @update:value="val => updatePaymentMode(val, payment.id)"
              />
              <CustomInput
                :key="`referenceNumber_${payment.id}_${rowRevertKey[payment.id] || 0}`"
                label-text="Reference Number"
                :value="payment.referenceNumber"
                :name="`referenceNumber_${payment.id}`"
                :state-key="`referenceNumber_${payment.id}`"
                :is-disabled="payment.isAdvance || updatePaymentStatus === 'pending'"
                @update:value="val => updateFormState(val, payment.id, 'referenceNumber')"
              />
              <CustomDropdown
                :key="`bookOfAccounts_${payment.id}_${rowRevertKey[payment.id] || 0}`"
                label-text="Book of Accounts"
                :value="payment.bookOfAccounts"
                :name="`bookOfAccounts_${payment.id}`"
                :state-key="`bookOfAccounts_${payment.id}`"
                :is-compulsory="true"
                :options="bookOfAccounts"
                label-width="158px"
                :disabled="payment.isAdvance || updatePaymentStatus === 'pending'"
                @update:value="val => updateBookOfAccounts(val, payment.id)"
              />
              <CustomDropdownWithSearch
                :key="`bankUid_${payment.id}_${rowRevertKey[payment.id] || 0}`"
                label-text="Bank Details"
                :value="payment.bankUid || ''"
                :name="`bankUid_${payment.id}`"
                :state-key="`bankUid_${payment.id}`"
                :options="bankOptionsFor(payment.bookOfAccounts)"
                :allow-clear="true"
                label-width="158px"
                :is-disabled="
                  !payment.bookOfAccounts || payment.paymentMode === 'Cash' || updatePaymentStatus === 'pending'
                "
                @update:value="val => updateFormState(val, payment.id, 'bankUid')"
              />
              <Transition name="payment-error">
                <a-alert v-if="payment.error" :message="payment.error" type="error" show-icon class="!rounded-md" />
              </Transition>
            </div>
            <div class="flex flex-col gap-2 bg-gray-50 p-3 rounded-r-lg justify-between">
              <div class="flex flex-col gap-3">
                <CustomRupeeInput
                  :key="`amount_${payment.id}_${rowRevertKey[payment.id] || 0}`"
                  label-text="Amount"
                  :value="payment.amount"
                  :name="`amount_${payment.id}`"
                  :is-compulsory="true"
                  :disabled="updatePaymentStatus === 'pending'"
                  :state-key="`amount_${payment.id}`"
                  @update:value="val => updateFormState(val, payment.id, 'amount')"
                />
                <div
                  v-if="payment.isAdvance && payment.advancePaymentId"
                  class="flex flex-col gap-2 bg-gray-50 p-3 rounded-lg border border-gray-200"
                >
                  <p class="text-gray-400 sm-medium">Payment Info</p>
                  <div class="flex items-center justify-between text-gray-600">
                    <p>Amount Paid</p>
                    <p>{{ formatToIndianCurrency(payment.amountPaid) }}</p>
                  </div>
                  <div class="flex items-center justify-between text-gray-600">
                    <p>Outstanding</p>
                    <p>{{ formatToIndianCurrency(payment.outstanding) }}</p>
                  </div>
                </div>
              </div>
              <div class="flex flex-col gap-2">
                <a-button
                  class="self-end !bg-gray-50 !border-none !shadow-none !text-red-600 !w-fit !h-fit !p-0 sm-medium"
                  :disabled="updatePaymentStatus === 'pending'"
                  @click="removePayment(payment.id)"
                >
                  Remove
                </a-button>
              </div>
            </div>
          </div>
        </a-form>
        <div class="flex gap-3 px-3 py-5 border-b border-gray-100 mb-10">
          <a-button
            class="flex items-center gap-2 !p-0 !w-fit !h-fit !border-none !bg-transparent !shadow-none text-blue-700"
            :disabled="
              isEditDisabled ||
              parseFloat(paymentStatus.payable) === parseFloat(paymentStatus.amount_paid) ||
              parseFloat(paymentStatus.amount_paid) >= parseFloat(paymentStatus.payable)
            "
            @click="addPayment(false)"
          >
            <PlusIcon class="h-4 w-4" />
            Add Payment
          </a-button>
          <!-- <a-button
            class="flex items-center gap-2 !p-0 !w-fit !h-fit !border-none !bg-transparent !shadow-none text-blue-700"
            :disabled="updatePaymentStatus === 'pending'"
            @click="addPayment(true)"
          >
            <PlusIcon class="h-4 w-4" />
            Link Advance
          </a-button> -->
        </div>
      </template>
      <template #column2>
        <div class="border border-zinc-200 bg-white rounded shadow-[0px_1px_3px_0px_#0000001A]">
          <h2 class="border-b-2 border-zinc-200 px-3 py-2 text-zinc-600 font-semibold">Advance Payment Info</h2>
          <div class="flex flex-col">
            <div class="px-3 py-2 flex flex-col gap-2">
              <div class="flex justify-between items-center">
                <div class="flex gap-1 items-center">
                  <p class="sm-medium text-zinc-500">Advance requested</p>
                  <InformationCircle title="Total Donations booked across the system" />
                </div>
                <p class="sm-medium">
                  {{ formatToIndianCurrency(paymentStatus.advance_requested) }}
                </p>
              </div>
              <div class="flex justify-between items-center">
                <div class="flex gap-1 items-center">
                  <p class="sm-medium text-zinc-500">TDS</p>
                  <InformationCircle title="Pledges / Grants tranches committed, for a past date but not completed" />
                </div>
                <p class="sm-medium">
                  {{ formatToIndianCurrency(paymentStatus.tds) }}
                </p>
              </div>
            </div>

            <div class="px-3 py-2 flex flex-col gap-2 border-t">
              <div class="flex justify-between items-center">
                <div class="flex gap-1 items-center">
                  <p class="sm-medium text-zinc-500">Payable</p>
                  <InformationCircle title="Pledges / Grants tranches upcoming, for a future date." />
                </div>
                <p class="sm-medium text-zinc-800">
                  {{ formatToIndianCurrency(paymentStatus.payable) }}
                </p>
              </div>
              <div class="flex justify-between items-center">
                <p class="sm-medium text-zinc-500">Amount Paid</p>
                <p class="sm-medium text-zinc-800">
                  {{ formatToIndianCurrency(paymentStatus.amount_paid) }}
                </p>
              </div>
            </div>
          </div>
          <div class="flex justify-between items-center border-t-2 border-zinc-200 px-3 py-2">
            <p class="sm-medium text-zinc-500">Balance</p>
            <p class="sm-medium text-[#DC2626]">
              {{ formatToIndianCurrency(paymentStatus.balance) }}
            </p>
          </div>
        </div>
        <!-- <div
          :class="`bg-white border border-gray-200 rounded shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] `"
        >
          <div class="flex justify-between w-full p-3">
            <span class="font-medium text-sm text-gray-600">Total Amount Paid</span>
            <span class="font-bold text-sm text-gray-700"> {{ expenseDetails.totalAmountPaid }}</span>
          </div>
        </div>
        <div
          class="flex flex-col bg-white text-gray-600 sm-medium w-full rounded shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] overflow-hidden"
        >
          <div class="flex items-center justify-between w-full p-3 border-b border-gray-200">
            <p>Balance Payments</p>
            <p class="text-red-700">{{ expenseDetails.balancePayments }}</p>
          </div>
          <div class="flex items-center justify-between w-full px-3 py-2 border-b border-gray-200">
            <div class="flex gap-2 items-center sm-medium">
              <ExclamationIcon class="h-4 w-4 text-red-600" />
              <p>Balance for FCRA</p>
            </div>
            <p>{{ expenseDetails.balanceForFCRA }}</p>
          </div>
          <div class="flex items-center justify-between w-full px-3 py-2 border-b border-gray-200">
            <p>Balance for Non-FCRA</p>
            <p>{{ expenseDetails.balanceForNonFCRA }}</p>
          </div>
          <div class="flex items-center justify-between w-full px-3 py-2 border-b border-gray-200">
            <p>Remaining to be Allocated</p>
            <p>{{ expenseDetails.remainingToBeAllocated }}</p>
          </div>
        </div> -->
        <!-- <ExpenseTotalAmountModal
          :total-invoice-amount="expenseDetails.totalInvoiceAmount"
          :allocation-details="expenseDetails.grantsAndBudgetAllocation"
        /> -->
        <AuditLogs :items="auditLogs" title="Expense" />
      </template>
    </DrawerLayout>
    <!-- <a-collapse
      v-model:active-key="activeKey"
      class="fixed bottom-0 right-0 w-[1220px] paymentCollapse"
      :default-active-key="['1']"
    > -->
    <!-- <template #expandIcon="{ isActive }">
        <ChevronDownIcon class="text-black w-4 h-4" :class="{ 'rotate-180': !isActive }" />
      </template> -->
    <!-- <a-collapse-panel key="1" header="Payment Summary" class="">
        <div class="rounded shadow-[0_1px_4px_0px_#0C0C0D0D] overflow-hidden">
          <table class="w-full">
            <thead>
              <tr class="bg-gray-50">
                <td class="border border-gray-200 w-full"></td>
                <td class="border border-gray-200 xs-medium text-gray-600 min-w-[140px] px-3 py-2 text-right">
                  Non-FCRA
                </td>
                <td class="border border-gray-200 xs-medium text-gray-600 min-w-[140px] px-3 py-2 text-right">FCRA</td>
                <td class="border border-gray-200 xs-medium text-gray-600 min-w-[140px] px-3 py-2 text-right">
                  Unallocated
                </td>
                <td class="border border-gray-200 xs-medium text-gray-600 min-w-[140px] px-3 py-2 text-right">Total</td>
              </tr>
            </thead>
            <tbody>
              <PaymentSummaryRow
                :expense-details="[
                  'Total Expense Allocation',
                  expenseDetails.totalExpenseAllocationNonFcra,
                  expenseDetails.totalExpenseAllocationFcra,
                  expenseDetails.totalUnallocatedTotalAmount,
                  expenseDetails.totalExpenseAllocation
                ]"
              />
              <PaymentSummaryRow
                :expense-details="[
                  'TDS',
                  expenseDetails.tdsNonFcra,
                  expenseDetails.tdsFcra,
                  expenseDetails.unallocatedTdsAmount,
                  expenseDetails.totalTdsAmount
                ]"
              />
              <PaymentSummaryRow
                :expense-details="[
                  'Payable (Net of TDS) - (A)',
                  expenseDetails.payableNetOfTdsNonFcra,
                  expenseDetails.payableNetOfTdsFcra,
                  expenseDetails.payableNetOfTdsUnallocated,
                  expenseDetails.payableNetOfTds
                ]"
              />
              <PaymentSummaryRow :expense-details="['Payments', paymentsNonFcra, paymentsFcra, '-', totalPayments]" />
              <PaymentSummaryRow
                :expense-details="[
                  'Payments via Advances',
                  paymentsViaAdvancesNonFcra,
                  paymentsViaAdvancesFcra,
                  '-',
                  totalPaymentsViaAdvances
                ]"
              />
              <PaymentSummaryRow
                :expense-details="[
                  'Total Amount Paid - (B)',
                  totalPaymentsAndAdvancesNonFcra,
                  totalPaymentsAndAdvancesFcra,
                  '-',
                  totalPaymentAndAdvancesBoth
                ]"
              />
              <PaymentSummaryRow
                :expense-details="[
                  'Balance',
                  balanceNonFcra,
                  balanceFcra,
                  expenseDetails.payableNetOfTdsUnallocated,
                  balanceBoth
                ]"
              />
            </tbody>
          </table>
        </div>
      </a-collapse-panel> -->
    <!-- </a-collapse> -->
    <a-modal
      :open="linkedChangeWarning.visible"
      title="This payment is linked to an expense"
      class="linked-advance-warning-modal"
      :closable="false"
      :mask-closable="false"
      width="480px"
      @cancel="cancelLinkedChange"
    >
      <div class="linked-advance-warning-body">
        <span class="linked-advance-warning-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <div class="linked-advance-warning-text">
          <p>
            This advance payment is already used in an expense. If you change it here, the linked expense
            <span class="font-semibold">won't update automatically</span>.
          </p>
          <p>You'll need to open that expense and update its payment details yourself.</p>
        </div>
      </div>
      <template #footer>
        <div class="linked-advance-warning-footer">
          <a-button class="linked-advance-warning-cancel-btn" @click="cancelLinkedChange">Cancel</a-button>
          <a-button type="primary" class="linked-advance-warning-proceed-btn" @click="confirmLinkedChange"
            >Proceed anyway</a-button
          >
        </div>
      </template>
    </a-modal>
  </a-drawer>
</template>

<style>
  @import '~/assets/css/donationDrawer.css';
  @import '~/assets/css/modal.css';

  .linked-advance-warning-modal.account-info-modal .ant-modal-body {
    padding: 20px;
  }

  .linked-advance-warning-body {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .linked-advance-warning-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 9999px;
    background-color: #fef3c7;
    color: #b45309;
  }

  .linked-advance-warning-text p {
    margin: 0;
    font-size: 13px;
    line-height: 20px;
    color: #52525b;
  }

  .linked-advance-warning-text p + p {
    margin-top: 8px;
  }

  .linked-advance-warning-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px;
    border-top: 1px solid #e5e7eb;
    background-color: #f9fafb;
    border-radius: 0 0 8px 8px;
  }

  .linked-advance-warning-cancel-btn {
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #71717a;
    border: 1px solid #d4d4d8;
    padding: 6px 12px;
    height: auto;
  }

  .linked-advance-warning-proceed-btn {
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    padding: 6px 12px;
    height: auto;
    background-color: #1d4ed8;
    border: 1px solid #1d4ed8;
  }

  .linked-advance-warning-proceed-btn:hover {
    background-color: #1e40af !important;
    border-color: #1e40af !important;
  }

  .paymentManagement .ant-picker {
    width: 206px;
  }

  .paymentManagement .ant-input {
    width: 206px;
  }

  .paymentManagement .ant-select-selector {
    width: 206px !important;
  }

  .paymentManagement .ant-select {
    width: 206px !important;
  }

  .paymentManagement .ant-form-item-control-input-content {
    width: 206px !important;
  }

  .paymentManagement .ant-input-number {
    width: 145px !important;
  }

  .paymentManagement .ant-form-item {
    margin-bottom: 0px;
  }

  .paymentCollapse .ant-collapse-expand-icon {
    margin-top: 1px;
    margin-right: 8px;
    border: 1px solid #d4d4d8;
    padding: 0px !important;
    background: #fff;
    height: 20px !important;
    border-radius: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    z-index: 1;
  }

  .paymentCollapse .ant-collapse-item {
    border-radius: 0px !important;
    border: 0px !important;
  }

  .paymentCollapse .ant-collapse {
    border-radius: 0px !important;
    border: 0px !important;
  }

  .paymentCollapse .ant-collapse-header {
    border-radius: 0px !important;
    border: 0px !important;
  }

  .paymentCollapse .ant-collapse-content {
    border-top: 0px !important;
  }

  .paymentCollapse .ant-collapse-content-box {
    padding: 12px !important;
    padding-top: 0px !important;
  }

  .payment-error-enter-active {
    animation: slideInError 0.5s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .payment-error-leave-active {
    animation: slideOutError 0.6s cubic-bezier(0.64, 0, 0.78, 0) forwards;
  }
  @keyframes slideInError {
    from {
      opacity: 0;
      max-height: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      max-height: 80px;
      transform: translateY(0);
    }
  }
  @keyframes slideOutError {
    from {
      opacity: 1;
      max-height: 80px;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      max-height: 0;
      transform: translateY(-4px);
    }
  }
</style>
